import {
  useAcceptTableServiceOrdersMutation,
  useCompleteTableServiceOrdersMutation,
  useRejectTableServiceOrdersMutation,
  useTableServiceOrdersQuery,
  useTableServiceSettingsMutation,
  useTableServiceSettingsQuery,
} from "@greeter-venue/api/Hooks";
import { DatePeriod } from "@greeter/date";
import { useInterval } from "@greeter/hooks";
import { mapQuery } from "@greeter/util";
import { addHours, hoursToMilliseconds, subHours } from "date-fns";
import { useEffect, useState } from "react";
import { Network } from "@capacitor/network";
import { TableServiceOverviewPage } from "./index";
import { logger } from "@greeter/log";
import { useGreeterVenueApi } from "@greeter/matter";

const name = "[TablerServiceOverviewPageApiHandler]";
const log = logger(name);

const createPeriodWith24HourSpan = () => {
  const now = new Date();
  return new DatePeriod({
    from: subHours(now, 24),
    to: addHours(now, 24),
  });
};

/**
 * Automattically updates a 24 hours span from "now".
 * It works like this. Once every hour, it updates to a new period.
 *
 * |----12 hours-----now-----12 hours----|
 */
const useAutoPeriod = () => {
  const [period, setPeriod] = useState<DatePeriod>(
    createPeriodWith24HourSpan()
  );

  useInterval(
    () => setPeriod(createPeriodWith24HourSpan()),
    hoursToMilliseconds(1),
    [period]
  );

  return period;
};

export const TableServiceOverviewApiHandler = () => {
  const api = useGreeterVenueApi();
  const period = useAutoPeriod();
  const tableServiceSettings = useTableServiceSettingsQuery({
    refetchInterval: 5000,
  });
  const tableServiceSettingsMutation = useTableServiceSettingsMutation();

  const acceptTableServiceOrder = useAcceptTableServiceOrdersMutation({
    period,
  });
  const completeTableServiceOrder = useCompleteTableServiceOrdersMutation({
    period,
  });
  const rejectTableServiceOrder = useRejectTableServiceOrdersMutation({
    period,
  });

  const tableServiceOrders = useTableServiceOrdersQuery(period, {
    refetchInterval: 5000,
    enabled:
      !acceptTableServiceOrder.isPending &&
      !completeTableServiceOrder.isPending &&
      !rejectTableServiceOrder.isPending,
  });

  useEffect(() => {
    Network.addListener("networkStatusChange", (status) => {
      log("Network status changed", status);
    });

    setInterval(async () => {
      const status = await Network.getStatus();

      log("Network status:", status);
    }, 2500);
  }, []);

  return (
    <TableServiceOverviewPage
      settings={mapQuery(tableServiceSettings)}
      tableServiceOrders={mapQuery(tableServiceOrders)}
      onAccept={(tso) => acceptTableServiceOrder.mutateAsync(tso.id)}
      onReject={(tso) => rejectTableServiceOrder.mutateAsync(tso.id)}
      onServe={(tso) => completeTableServiceOrder.mutateAsync(tso.id)}
      onChangeMode={(mode) =>
        tableServiceSettingsMutation.mutateAsync({ mode })
      }
      onRefresh={async () => await tableServiceOrders.refetch()}
    />
  );
};
